import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { guestApi } from '../api';
import { GuestBookingResponse } from '../api_types';
import { Spinner } from '../shared/Spinner';
import { format, parseISO } from 'date-fns';
import './styles/GuestBookingSuccess.css';

export const GuestBookingSuccess: React.FC = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [booking, setBooking] = useState<GuestBookingResponse | null>(null);
  const [emailSent, setEmailSent] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const confirmAndFetch = async () => {
      if (!bookingId) {
        setError('No booking ID provided');
        setLoading(false);
        return;
      }

      const sessionId = searchParams.get('session_id');
      const businessId = searchParams.get('businessId');
      const facilityId = searchParams.get('facilityId');

      if (!sessionId || !businessId || !facilityId) {
        setError('Missing session_id, businessId, or facilityId in URL');
        setLoading(false);
        return;
      }

      try {
        // 2) confirm payment with backend
        await guestApi.confirmBooking(bookingId, sessionId, businessId, facilityId);

        // 3) fetch the now-confirmed booking
        const confirmed = await guestApi.verifyBookingStatus(bookingId, businessId, facilityId);
        setBooking(confirmed);

        // 4) send confirmation email
        if (confirmed && !emailSent) {
          try {
            await guestApi.sendBookingConfirmationEmail(bookingId, businessId, facilityId);
            setEmailSent(true);
          } catch (emailErr) {
            console.error('Error sending confirmation email:', emailErr);
            // Don't set error state here as the booking was successful
          }
        }
      } catch (err: any) {
        console.error('Error confirming or fetching booking:', err);
        setError(err.message || 'Failed to confirm booking');
      } finally {
        setLoading(false);
      }
    };

    confirmAndFetch();
  }, [bookingId, searchParams, emailSent]);

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours, 10);
    const period = h >= 12 ? 'PM' : 'AM';
    const displayHour = h > 12 ? h - 12 : h === 0 ? 12 : h;
    return `${displayHour}:${minutes} ${period}`;
  };

  const formatDate = (dateString: string) => {
    try {
      // Parse the ISO date string and format it consistently
      const date = parseISO(dateString);
      return format(date, 'MMMM d, yyyy');
    } catch (err) {
      console.error('Error formatting date:', err);
      return dateString; // Fallback to original string if parsing fails
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <Spinner />
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="empty-state">
        <h3 className="empty-state-title">Error</h3>
        <p className="empty-state-text">{error}</p>
      </div>
    );
  }

  return (
    <div className="booking-success">
      <div className="success-header">
        <svg
          className="success-icon"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <h1>Booking Confirmed!</h1>
      </div>

      <div className="booking-details">
        <section className="detail-section">
          <h2>Reservation Details</h2>
          <div className="detail-grid">
            <div className="detail-item">
              <span className="label">Date:</span>
              <span className="value">
                {formatDate(booking.courtDetails.date)}
              </span>
            </div>
            <div className="detail-item">
              <span className="label">Time:</span>
              <span className="value">
                {formatTime(booking.courtDetails.startTime)} –{' '}
                {formatTime(booking.courtDetails.endTime)}
              </span>
            </div>
            <div className="detail-item">
              <span className="label">Confirmation Code:</span>
              <span className="value highlight">
                {booking.confirmationCode}
              </span>
            </div>
            <div className="detail-item">
              <span className="label">Amount Paid:</span>
              <span className="value">
                ${(booking.amount / 100).toFixed(2)}
              </span>
            </div>
          </div>
        </section>

        <section className="detail-section">
          <h2>Guest Information</h2>
          <div className="detail-grid">
            <div className="detail-item">
              <span className="label">Name:</span>
              <span className="value">{booking.guestDetails.name}</span>
            </div>
            <div className="detail-item">
              <span className="label">Email:</span>
              <span className="value">{booking.guestDetails.email}</span>
            </div>
            <div className="detail-item">
              <span className="label">Phone:</span>
              <span className="value">{booking.guestDetails.phone}</span>
            </div>
          </div>
        </section>

        {booking.receiptUrl && (
          <div className="receipt-section">
            <a
              href={booking.receiptUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="receipt-button"
            >
              View Receipt
            </a>
          </div>
        )}

        <section className="next-steps">
          <h2>Next Steps</h2>
          <ul>
            <li>
              Save your confirmation code: <strong>{booking.confirmationCode}</strong>
            </li>
            <li>
              A confirmation email has been sent to {booking.guestDetails.email}
            </li>
            <li>Please arrive 10 minutes before your scheduled time</li>
            <li>Bring appropriate court shoes and equipment</li>
          </ul>
        </section>

        <div className="guest-back-button-container">
          <button
            className="guest-back-button"
            onClick={() => navigate('/guest')}
          >
            ← Back to Guests
          </button>
        </div>

      </div>
    </div>
  );
};
